/**
 * The one-paste handoff to an AI coding agent (migration 070; design:
 * specs/012-widget-partner-portal/agentic-setup.md). Pure functions so the
 * onboarding card, the AI agent access page, the docs page and the tests all
 * read one source.
 *
 * The command registers the FinderlyFix MCP server with Claude Code for the
 * current project (Claude Code's default "local" scope: stored in the user's
 * own Claude Code config, not in the repository) and starts a session with
 * the setup request. It must be run in a terminal at the repository root: a
 * server added from inside a running session is not loaded until the next one.
 *
 * Two forms (T097). The default carries no credential at all: `claude mcp
 * login` opens the browser, the person signs in to this portal and approves
 * the agent on the consent screen, and Claude Code keeps short-lived OAuth
 * tokens it refreshes itself — nothing lands in shell history. The header
 * form, with an `fxa_` token minted here and shown once, stays for MCP
 * clients that cannot do the browser sign-in.
 */
import { API_BASE, PORTAL_ORIGIN } from '../env';

export const DOCS_PATH = '/docs/ai-setup';
export const MCP_SERVER_NAME = 'finderlyfix';
export const TOKEN_ENV_VAR = 'FINDERLYFIX_AGENT_TOKEN';

/** Short on purpose: the MCP server's own instructions carry the playbook. */
export const SETUP_PROMPT = 'Set up FinderlyFix on this site';

/**
 * The newcomer path (no account yet): ONE prompt the person pastes into their
 * agent, which signs the site up (`/partner/signup/*`), connects the MCP
 * server with the returned token, installs the widget and sends a test lead.
 * It points at `/agent-setup.md`, which the portal serves as a static file
 * rendered from the server's playbook (agentSetupFiles.ts). The words must
 * equal the server's `newcomerPrompt` (agent.test.tsx checks), and the
 * homepage's /widget card shows the same prompt.
 */
export const AGENT_SETUP_PATH = '/agent-setup.md';

export function agentSetupUrl(origin: string = PORTAL_ORIGIN): string {
  return `${origin.replace(/\/+$/, '')}${AGENT_SETUP_PATH}`;
}

export function newcomerPrompt(origin: string = PORTAL_ORIGIN): string {
  return `Sign this website up for FinderlyFix and install the widget. Follow ${agentSetupUrl(origin)}`;
}

/** The same prompt as a one-line terminal command, for a person who starts their agent from the shell. */
export function newcomerCommand(cli: 'claude' | 'codex', origin: string = PORTAL_ORIGIN): string {
  return `${cli} "${newcomerPrompt(origin)}"`;
}

export function mcpUrl(apiBase: string = API_BASE): string {
  return `${apiBase.replace(/\/+$/, '')}/partner/mcp`;
}

/**
 * The single paste, OAuth form (T097): register the server with no header,
 * sign in through the browser, then start Claude Code with the request.
 */
export function claudeCodeOAuthCommand(apiBase: string = API_BASE): string {
  return [
    `claude mcp add --transport http ${MCP_SERVER_NAME} ${mcpUrl(apiBase)} \\`,
    `  && claude mcp login ${MCP_SERVER_NAME} \\`,
    `  && claude "${SETUP_PROMPT}"`,
  ].join('\n');
}

/**
 * Other agents (checked against each client's docs, 2026-09-27). The MCP
 * server signs clients in with Client ID Metadata Documents only (no Dynamic
 * Client Registration, services/partner-oauth-clients.ts). Codex, VS Code and
 * Claude's custom connectors publish one that the server accepts
 * (server tests/unit/partner-oauth.test.ts holds their documents); Cursor
 * registers dynamically only, so it connects with an API key header.
 */

/** OpenAI Codex CLI: `codex mcp add` writes ~/.codex/config.toml; `codex mcp login` signs in (CIMD). */
export function codexCommand(apiBase: string = API_BASE): string {
  return [
    `codex mcp add ${MCP_SERVER_NAME} --url ${mcpUrl(apiBase)} \\`,
    `  && codex mcp login ${MCP_SERVER_NAME} \\`,
    `  && codex "${SETUP_PROMPT}"`,
  ].join('\n');
}

/** VS Code (Copilot agent mode): adds the server to the user profile; VS Code asks to sign in on first use. */
export function vsCodeCommand(apiBase: string = API_BASE): string {
  return `code --add-mcp '${JSON.stringify({ name: MCP_SERVER_NAME, type: 'http', url: mcpUrl(apiBase) })}'`;
}

/** Cursor has no metadata-document sign-in, so its `.cursor/mcp.json` carries an API key from the environment. */
export function cursorMcpJson(apiBase: string = API_BASE): string {
  return JSON.stringify(
    { mcpServers: { [MCP_SERVER_NAME]: { url: mcpUrl(apiBase), headers: { Authorization: `Bearer \${env:${TOKEN_ENV_VAR}}` } } } },
    null,
    2,
  );
}

/**
 * `claude mcp login` finishes the sign-in on a TTY, so it fails from a script
 * or CI ("stdin isn't a terminal"). Found in a live run with Claude Code
 * 2.1.283; shown under the one-paste command on the Install step and the docs.
 */
export const INTERACTIVE_TERMINAL_NOTE =
  'claude mcp login needs an interactive terminal (pasting the command into one is enough); from a script or CI, use an API key instead.';

/** The OAuth form in `.mcp.json`: no header, nothing secret, safe to commit. */
export function mcpJsonOAuthConfig(apiBase: string = API_BASE): string {
  return JSON.stringify({ mcpServers: { [MCP_SERVER_NAME]: { type: 'http', url: mcpUrl(apiBase) } } }, null, 2);
}

/** The single paste, header form: register the server with a token, then start Claude Code. */
export function claudeCodeCommand(token: string, apiBase: string = API_BASE): string {
  return [
    `claude mcp add --transport http ${MCP_SERVER_NAME} ${mcpUrl(apiBase)} \\`,
    `  --header "Authorization: Bearer ${token}" \\`,
    `  && claude "${SETUP_PROMPT}"`,
  ].join('\n');
}

/**
 * For other MCP clients, or a team that wants the server in `.mcp.json`: the
 * token stays in an environment variable, never in the file. Claude Code
 * expands `${VAR}` in `.mcp.json`; other clients may need the value inline in
 * their own (uncommitted) config.
 */
export function mcpJsonConfig(apiBase: string = API_BASE): string {
  return JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_NAME]: {
          type: 'http',
          url: mcpUrl(apiBase),
          headers: { Authorization: `Bearer \${${TOKEN_ENV_VAR}}` },
        },
      },
    },
    null,
    2,
  );
}

/** The generated OpenAPI document for the agent-reachable routes (public). */
export function openApiUrl(apiBase: string = API_BASE): string {
  return `${apiBase.replace(/\/+$/, '')}/partner/openapi.json`;
}

/** A plain HTTP call, for agents that do not speak MCP. */
export function curlExample(apiBase: string = API_BASE): string {
  return `curl -s ${apiBase.replace(/\/+$/, '')}/partner/agent/whoami \\\n  -H "Authorization: Bearer $${TOKEN_ENV_VAR}"`;
}

/**
 * What the agent does, short and in full (the AI agent access page shows the
 * short words on one line and the full sentences behind "What an agent can
 * and can't do"). `tools` are the MCP tools that do it; agent.test.tsx checks
 * each one exists on the server, so the page cannot promise a tool that is
 * gone.
 */
export interface AgentDoes {
  short: string;
  tools: readonly string[];
}

export const AGENT_DOES: readonly AgentDoes[] = [
  { short: 'install', tools: ['get_install_instructions', 'add_site_origin'] },
  { short: 'verify', tools: ['verify_install', 'verify_install_rendered'] },
  { short: 'match your colours', tools: ['check_branding', 'update_widget_config'] },
  { short: 'set trades & area', tools: ['set_trades_and_area'] },
  { short: 'add contractors & destinations', tools: ['add_contractor', 'import_contractors_csv', 'activate_contractors', 'request_destination'] },
  { short: 'send a test lead', tools: ['send_test_lead'] },
];

/** What the agent does, in full. Kept to what the tools actually do. */
export const AGENT_WILL: readonly string[] = [
  'Add the widget snippet to the page template you choose',
  'Add the ownership file and run verification after you deploy',
  'Read your CSS and propose colours, radius and font that pass the same contrast checks as this portal',
  'Set trades, service area, contractors, where leads go and routing rules from what you tell it',
  'Change consent text, notices, recording and the emergency screen when you ask it to (owners are emailed the old and new wording)',
  'Add lead destinations, switch contractors on and, when you ask, switch the Network fallback on or lift a contractor’s opt-out the contractor asked to lift: these take effect at once, and owners and admins are emailed right away with a one-click Undo',
  'Send a test lead and report what happened',
  'Accept an updated version of the Partner Terms for your organisation when you tell it to (owners and admins are emailed)',
  'Invite the teammates you name, in any role but owner, once an owner or admin approves it; they join only by accepting the emailed invitation, which says an AI agent sent it for your organisation',
  'Renew its own token before it expires: the same access for the same person, at most once a day and never past 30 days after you granted it (owners and admins are emailed)',
  'No account yet? Sign your organisation up itself, once you name a person authorised to bind it who OKs the Partner Terms; that person then claims the account',
];

/**
 * What stays with a person, grouped: the short SECURITY list (none of it is a
 * person's act in law — specs/012-widget-partner-portal/agentic-setup.md
 * §"Human-only, and why"). The single source for the short "Needs you" line,
 * the full list (docs, OAuth consent, the detail disclosure) and the parity
 * test against the server:
 *
 *   - `playbook`: the server playbook's own words for it
 *     (services/partner-agent-playbook.ts PLAYBOOK_HUMAN_ONLY, verbatim) —
 *     every playbook item must belong to exactly one group;
 *   - `enforced`: where the server refuses it to an agent
 *     (services/partner-agent-policy.ts): `route:METHOD /path` is refused as
 *     HUMAN_ONLY, `config:path` is a HUMAN_ONLY_CONFIG_RULES field (every one
 *     of those must be named by a group), `body:METHOD /path:field` is a body
 *     guard that refuses that field outright (not a delayed change).
 */
export interface HumanOnlyGroup {
  short: string;
  full: readonly string[];
  playbook: readonly string[];
  enforced: readonly string[];
}

export const NEEDS_YOU: readonly HumanOnlyGroup[] = [
  {
    // Migration 080 (owner decisions 2026-09-27): an agent with no account
    // can sign the organisation up itself and accept the Partner Terms for it
    // on a named person's authority; that person then CLAIMS the account by
    // signing in with the named email and confirming. The claim is theirs.
    // After the organisation's first acceptance (a person's in the portal, or
    // the one made at agent sign-up) an agent may accept an updated version.
    short: 'claiming an agent sign-up',
    full: ['Claiming an account an AI agent signed up: the person it named signs in with that email and confirms'],
    playbook: ['claim an account an agent signed up (the person it named signs in with that email and confirms)'],
    enforced: ['route:POST /org/claim', 'route:POST /org'],
  },
  {
    // Migration 083: an agent may INVITE (after a window, never as owner) and
    // renew its own token; roles, removal, ownership and granting or
    // approving agent access stay with a person.
    short: 'team roles & agent access',
    full: ['Changing roles, removing teammates or making someone an owner, and creating or approving agent access'],
    playbook: ['change team roles, remove team members or make anyone an owner, or create or approve agent access'],
    enforced: ['route:PATCH /org/members/:id', 'route:DELETE /org/members/:id', 'route:POST /agent-tokens', 'route:POST /pending-changes/:id/approve'],
  },
  {
    short: 'keys & secrets',
    full: ['Revealing or rotating keys and secrets'],
    playbook: ['reveal or rotate keys and secrets'],
    enforced: ['route:POST /sites/:site_key/rotate', 'route:POST /destinations/:id/rotate-secret'],
  },
  {
    short: 'reading leads & sharing stats',
    full: ['Reading, exporting or sharing leads (homeowner data)', 'Sharing the dashboard outside your organisation (stats links)'],
    playbook: ['read, export or share leads (homeowner data)', 'share the dashboard outside the organisation (stats links)'],
    enforced: ['route:GET /leads', 'route:POST /dashboard/export', 'route:POST /dashboard/share-link', 'route:GET /dashboard/share-links'],
  },
  {
    // Billing, fees and plan are human-only routes, so an agent cannot reach
    // them: "not available to agents", not a gate. The plan field in the
    // widget config (`branding.powered_by`) is the one refused.
    short: 'billing & retention',
    full: ['Billing, fees and plan settings (human-only: a person accepts the fee schedule and reads the statement)', 'How long homeowner photos and recordings are kept'],
    playbook: ['change billing, fees or plan settings (human-only: a person accepts the fee schedule and reads the statement in the portal)', 'change how long homeowner media is kept'],
    enforced: ['config:branding.powered_by', 'body:PATCH /org:media_retention_days'],
  },
  {
    // Migration 092: an agent can pause the widget and give up its own token,
    // but closing the organisation and deleting its data is an owner's
    // request to FinderlyFix staff (Organisation → Close organisation).
    short: 'closing the organisation',
    full: ['Closing your organisation and deleting its data: an owner asks on Organisation → Close organisation, or emails partners@finderlyfix.com'],
    playbook: ['close the organisation or delete its data (an owner asks in the portal, or emails partners@finderlyfix.com)'],
    enforced: ['route:POST /org/close-request'],
  },
];

/**
 * What an agent does that owners and admins are told about at once, with a
 * one-click Undo (migration 096; before it, 074's window). The name is 074's.
 * `held` marks the one kind that still waits for an owner's or admin's
 * approval (server HELD_KINDS). `enforced` is where the server sends an agent
 * to the tracked route: `route:METHOD /path` answers DELAYED,
 * `body:METHOD /path:field` is a body guard that points at a pending-change
 * kind, `publish:path` is a widget config contact target whose agent publish
 * is recorded with an Undo (CONTACT_TARGET_CONFIG_PATHS, migration 086).
 * agent.test.tsx checks all three.
 */
export interface DelayedGroup {
  short: string;
  full: string;
  enforced: readonly string[];
  /** Waits for an owner's or admin's approval instead of applying at once. */
  held?: boolean;
}

export const AFTER_A_WINDOW: readonly DelayedGroup[] = [
  {
    short: 'lead destinations',
    full: 'Adding a lead destination (webhook or email) or changing where one sends leads',
    enforced: ['route:POST /destinations', 'body:PATCH /destinations/:id:config'],
  },
  {
    short: 'switching contractors on',
    full: 'Switching contractors on, after you confirm to the agent that your organisation has bound them, in writing, to the contractor contact rules',
    enforced: ['body:POST /vendors:active', 'body:PATCH /vendors/:id:active', 'body:POST /vendors/bulk:active'],
  },
  {
    short: 'escalation contacts',
    full: 'Who is copied on lead notices (escalation contacts)',
    enforced: ['body:PATCH /routing:escalation'],
  },
  {
    // Migration 084: only the contractor lifts their opt-out. The agent
    // records what the contractor told you (how, when, what they said);
    // setting do-not-contact stays immediate.
    short: 'lifting a contractor opt-out',
    full: 'Lifting a contractor’s opt-out, only when the contractor told you they want offers again (the agent records how, when and what they said)',
    enforced: ['body:PATCH /vendors/:id:do_not_contact', 'route:POST /vendors/:id/reconsent'],
  },
  {
    // Migration 082: both are immediate; an agent's "on" is emailed with an Undo.
    short: 'network fallback on',
    full: 'Switching the Network fallback on (switching it off takes effect at once)',
    enforced: ['body:PUT /sites/:site_key/network-fallback:enabled'],
  },
  {
    // Migration 083: any role but owner; not before an agent-created account
    // is claimed. The invitee's email says an AI agent sent it.
    short: 'inviting teammates',
    full: 'Inviting a teammate in any role but owner (not before an account an agent signed up is claimed): this one waits for an owner or admin to approve it; they then join only by accepting the emailed invitation, which says an AI agent sent it for your organisation, and you can revoke it on Team until then',
    enforced: ['route:POST /org/members/invite'],
    held: true,
  },
  {
    // Migration 086: an agent's PUBLISH that moves one of these is recorded
    // with an Undo (096: live at once); a person's publish is not.
    short: 'new numbers and links for homeowners',
    full: 'Publishing widget settings that send homeowners to a new phone number or link (the call or booking button, the emergency number, the link back to your site)',
    enforced: ['publish:routing.actions[].phone', 'publish:routing.actions[].url', 'publish:safety.emergency_phone', 'publish:handoff.return_url'],
  },
];

/** One sentence on how these work (migration 096), for the page and the docs. */
export const WINDOW_NOTE =
  'These take effect at once. Owners and admins are emailed right away with what changed (before and after) and a one-click Undo link that works for 7 days, and see it at the top of the dashboard with Undo. Only an invitation waits: an owner or admin approves or rejects it, from the dashboard or the email.';

/**
 * The groups a person-only FIELD belongs to (a widget config field or a
 * refused body field), in NEEDS_YOU's words: what `AGENT_HUMAN_ONLY_FIELD`
 * means. Generated so the docs cannot drift from the list.
 */
export const HUMAN_ONLY_FIELD_GROUPS: readonly string[] = NEEDS_YOU.filter((g) =>
  g.enforced.some((e) => e.startsWith('config:') || e.startsWith('body:')),
).map((g) => g.short);

/** "a, b or c" */
export function listInWords(items: readonly string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/** What stays with a person, in the portal: every group's full sentences. */
export const HUMAN_ONLY: readonly string[] = NEEDS_YOU.flatMap((g) => g.full);
